import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Stack,
  IconButton,
} from '@mui/material';
import { fetchReleaseNotes } from '../../modules/admin';
import { formatDate } from '../../lib/utils';
import { RELEASE_NOTE_ITEM_ALLOWED_TYPES } from '../../static/common';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

const ReleaseUpdatesPage = () => {
  const [loading, setLoading] = useState(true);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [total, setTotal] = useState(0);

  const fetchReleaseNotesList = async () => {
    setLoading(true);
    try {
      const response = await fetchReleaseNotes(page, perPage);
      setReleaseNotes(response?.release_notes || []);
      setTotal(response?.total || 0);
    } catch (error) {
      console.error('Error fetching release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReleaseNotesList();
  }, [page]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Updates
      </Typography>

      {loading ? (
        <Box
          className="d-flex justify-content-center align-items-center"
          style={{ height: 200 }}>
          <Spinner animation="border" />
        </Box>
      ) : (
        <>
          {releaseNotes.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              No updates available.
            </Typography>
          ) : (releaseNotes.map((note) => (
            <Card key={note.id} sx={{ mb: 2 }}>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {note.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {note.version}
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Released on {formatDate(note.released_at)}
                </Typography>

                <Typography variant="body1" sx={{ mt: 1.5, mb: 2 }}>
                  {note.description}
                </Typography>

                {RELEASE_NOTE_ITEM_ALLOWED_TYPES.map((type) => {
                  const groupedItems = note.items?.filter(
                    (item) => item.type === type
                  );
                  if (!groupedItems?.length) return null;

                  return (
                    <Box key={type} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ mt: 1 }}>
                        {type}
                      </Typography>
                      <Box component="ul">
                        {groupedItems.map((item, index) => (
                          <Box component="li" key={index} sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" component="span">
                              {item.title}:{' '}
                            </Typography>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary">
                              {item.description}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )))}

          {totalPages > 1 && (
            <Row className="mt-4">
              <Col className="d-flex justify-content-center align-items-center gap-2">
                <IconButton
                  onClick={() => setPage((prev) => prev - 1)}
                  disabled={page === 1}>
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="body2">
                  Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                </Typography>
                <IconButton
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page === totalPages}>
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Col>
            </Row>
          )}
        </>
      )}
    </Box>
  );
};

export default ReleaseUpdatesPage;
